import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
  inject,
} from "@angular/core";
import {TranslateModule} from "@ngx-translate/core";
import {CdkScrollable} from "@angular/cdk/scrolling";
import {WorksTypes} from "src/app/enums/Works.enum";
import {
  DiscussionEntry,
  DiscussionParticipant,
  DiscussionDraft,
  DiscussionScrollAnchor,
} from "../../interfaces/discussion.interface";
import {DiscussionMessageComponent} from "../discussion-message/discussion-message.component";
import {DiscussionComposerComponent} from "../discussion-composer/discussion-composer.component";
import type {PublicWorkResponse} from "src/app/modules/works/interfaces/public-work.interface";

@Component({
  selector: "app-discussion-thread",
  templateUrl: "./discussion-thread.component.html",
  imports: [
    TranslateModule,
    CdkScrollable,
    DiscussionMessageComponent,
    DiscussionComposerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiscussionThreadComponent implements OnChanges, AfterViewChecked {
  @Input() entries: DiscussionEntry[] = [];
  @Input() participants: DiscussionParticipant[] = [];
  @Input() userId = "";
  @Input() recipient = "";
  @Input() suggestions: string[] = [];
  @Input() canReply = false;
  @Input() hasMore = false;
  @Input() loadingMore = false;
  @Input() automaticPagination = true;
  @Input() sending = false;
  @Input() deleting = false;
  @Input() typingVisible = false;
  @Input() friendOnline = false;
  @Input() peerReadSequence = 0;
  @Output() typing = new EventEmitter<boolean>();
  @Input() sentRevision = 0;
  @Input() unreadAfter?: number;
  @Input() originDate?: string;
  @Input() locale = "en";
  @Input() workType?: WorksTypes;
  @Output() sendMessage = new EventEmitter<DiscussionDraft>();
  @Output() removeMessage = new EventEmitter<string>();
  @Output() reportMessage = new EventEmitter<string>();
  @Output() openWork = new EventEmitter<PublicWorkResponse>();
  @Input() readOnlyReason = "DISCUSSION.READ_ONLY";
  @Output() loadMore = new EventEmitter<void>();
  @Output() scrolled = new EventEmitter<boolean>();
  newMessages = 0;
  private readonly cdr = inject(ChangeDetectorRef);
  private destroyed = false;
  private olderScheduled = false;
  @ViewChild("scroll") scroll?: ElementRef<HTMLDivElement>;
  private lastSequence?: number;
  private scrollToEnd = true;
  private previousHeight?: number;
  private initialPosition = true;
  timeline: DiscussionEntry[] = [];
  private anchor?: DiscussionScrollAnchor;
  private pinned = true;
  private observer?: ResizeObserver;

  @ViewChild("content") set content(ref: ElementRef<HTMLElement> | undefined) {
    this.observer?.disconnect();
    if (!ref) return;
    this.observer = new ResizeObserver(() => this.restorePosition());
    this.observer.observe(ref.nativeElement);
  }

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.destroyed = true;
      this.observer?.disconnect();
    });
  }

  onScroll(): void {
    this.rememberPosition();
    const view = this.scroll?.nativeElement;
    if (!view) return;
    this.scrolled.emit(view.scrollTop > 0);
    if (this.pinned) this.newMessages = 0;
    if (view.scrollTop < 100) this.older(true);
  }

  bottom(): void {
    const view = this.scroll?.nativeElement;
    if (view) view.scrollTop = view.scrollHeight;
    this.newMessages = 0;
    this.rememberPosition();
  }

  rememberPosition(): void {
    const view = this.scroll?.nativeElement;
    if (!view) return;
    this.pinned = view.scrollHeight - view.scrollTop - view.clientHeight < 24;
    const top = view.getBoundingClientRect().top;
    const element = Array.from(
      view.querySelectorAll<HTMLElement>("[data-discussion-anchor]")
    ).find((item) => item.getBoundingClientRect().bottom > top);
    this.anchor = element
      ? {
          id: element.dataset["discussionAnchor"]!,
          offset: element.getBoundingClientRect().top - top,
        }
      : undefined;
  }

  private restorePosition(): void {
    const view = this.scroll?.nativeElement;
    if (!view || this.initialPosition || this.loadingMore || this.scrollToEnd)
      return;
    if (this.pinned) view.scrollTop = view.scrollHeight;
    else if (this.anchor) {
      const anchor = this.anchor;
      const element = Array.from(
        view.querySelectorAll<HTMLElement>("[data-discussion-anchor]")
      ).find((item) => item.dataset["discussionAnchor"] === anchor.id);
      if (element)
        view.scrollTop +=
          element.getBoundingClientRect().top -
          view.getBoundingClientRect().top -
          anchor.offset;
    }
    this.rememberPosition();
  }

  ngOnChanges() {
    this.timeline = [...this.entries].sort(
      (a, b) =>
        Number(!!b.dateUnknown) - Number(!!a.dateUnknown) ||
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime() ||
        a.sequence - b.sequence
    );
    const last = Math.max(0, ...this.entries.map((entry) => entry.sequence));
    if (last !== this.lastSequence) {
      const newer = this.entries.filter(
        (entry) =>
          entry.kind === "message" &&
          entry.sequence > (this.lastSequence ?? last)
      );
      this.scrollToEnd =
        this.initialPosition ||
        this.pinned ||
        newer.some((entry) => entry.authorId === this.userId);
      if (!this.scrollToEnd)
        this.newMessages += newer.filter(
          (entry) =>
            entry.authorId !== this.userId &&
            !entry.deletedAt &&
            !entry.moderatedAt
        ).length;
      this.lastSequence = last;
    }
  }

  ngAfterViewChecked() {
    const el = this.scroll?.nativeElement;
    if (!el) return;
    if (this.previousHeight !== undefined && !this.loadingMore) {
      this.previousHeight = undefined;
      this.restorePosition();
      this.rememberPosition();
    } else if (this.scrollToEnd) {
      el.scrollTop = el.scrollHeight;
      this.scrollToEnd = false;
      this.initialPosition = false;
      this.rememberPosition();
    }
    if (
      el.scrollHeight <= el.clientHeight + 1 &&
      this.hasMore &&
      this.automaticPagination &&
      !this.loadingMore &&
      !this.olderScheduled
    ) {
      this.olderScheduled = true;
      queueMicrotask(() => {
        this.olderScheduled = false;
        if (this.destroyed) return;
        this.older(true);
        this.cdr.markForCheck();
      });
    }
  }

  older(automatic = false) {
    if (
      !this.hasMore ||
      this.loadingMore ||
      this.previousHeight !== undefined ||
      (automatic && !this.automaticPagination)
    )
      return;
    this.rememberPosition();
    this.previousHeight = this.scroll?.nativeElement.scrollHeight;
    this.loadMore.emit();
  }

  author(id: string) {
    return this.participants.find((person) => person._id === id);
  }
  newDay(index: number) {
    if (this.timeline[index].dateUnknown) return false;
    const previous =
      index === 0 || this.timeline[index - 1].dateUnknown
        ? this.originDate
        : this.timeline[index - 1].createdAt;
    return (
      !previous ||
      new Date(previous).toDateString() !==
        new Date(this.timeline[index].createdAt).toDateString()
    );
  }

  dateLabel(value: string): string {
    return new Date(value).toLocaleDateString(this.locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }
  firstUnread(index: number) {
    const entry = this.timeline[index];
    return (
      this.unreadAfter !== undefined &&
      entry.sequence > this.unreadAfter &&
      entry.authorId !== this.userId &&
      entry.kind === "message" &&
      !this.timeline
        .slice(0, index)
        .some(
          (previous) =>
            previous.sequence > this.unreadAfter! &&
            previous.authorId !== this.userId &&
            previous.kind === "message"
        )
    );
  }
}
